import type { Level } from '@/types'

// Fractions of the live total place count, not fixed counts — see the
// minFraction doc comment in types/badges.ts for why. Shape/proportions
// mirror the original design (each tier meaningfully harder than the
// last, top rank around ~87% rather than requiring literally everything).
export const LEVELS: Level[] = [
  { level: 1, title: 'New Explorer', emoji: '🧭', minFraction: 0 },
  { level: 2, title: 'Junior Explorer', emoji: '🗺️', minFraction: 0.1 },
  { level: 3, title: 'Trail Blazer', emoji: '🎒', minFraction: 0.2 },
  { level: 4, title: 'Globe Trotter', emoji: '🌍', minFraction: 0.35 },
  { level: 5, title: 'World Wanderer', emoji: '✈️', minFraction: 0.51 },
  { level: 6, title: 'Master Explorer', emoji: '🧗', minFraction: 0.7 },
  { level: 7, title: 'Legendary Explorer', emoji: '👑', minFraction: 0.87 },
]

export interface LevelInfo {
  level: number
  title: string
  emoji: string
  /** null once at the last level — there's nothing further to progress toward */
  nextMinPlaces: number | null
  /** 0..1, how far into the current level the explorer is; 1 when maxed out */
  progressToNext: number
}

/**
 * Level is derived from total places visited against the live total place
 * count, never stored directly — one source of truth, no drift, and no
 * risk of the level table going stale as more content ships.
 */
export function getLevelInfo(visitedCount: number, totalPlaces: number): LevelInfo {
  const thresholds = LEVELS.map((lvl) => ({ ...lvl, minPlaces: Math.round(lvl.minFraction * totalPlaces) }))

  let current = thresholds[0]
  for (const lvl of thresholds) {
    if (visitedCount >= lvl.minPlaces) current = lvl
  }
  const next = thresholds.find((lvl) => lvl.minPlaces > visitedCount) ?? null

  return {
    level: current.level,
    title: current.title,
    emoji: current.emoji,
    nextMinPlaces: next?.minPlaces ?? null,
    progressToNext: next
      ? (visitedCount - current.minPlaces) / (next.minPlaces - current.minPlaces)
      : 1,
  }
}
